import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage, useSelectMessages } from '@tmapy/intl';
import {
  BtnBar,
  DangerAlert,
  Form,
  FormItem,
  RadioGroup,
  SecondaryBtn,
  CtaBtn,
  Textarea,
  WaitingAlert,
} from '@tmapy/style-guide';

import type { UseResource } from '../../hooks/useResource';
import type { DocsRetrieveResponse } from '../../openapi/operations/docsRetrieve';
import { PrejimkaTypesEnum } from '../../openapi/types/PrejimkaTypesEnum';

import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';

import { REJECTION_REASON, REJECTION_REASON_DEFAULT, REJECTION_REASON_DTM_GAD } from '../constants';

import { rejectionReasonMessages, rejectionMessages } from './docsMessages';
interface RejectionProps {
  docId: number;
  prejimkaType?: PrejimkaTypesEnum;
  resource: UseResource<DocsRetrieveResponse>;
  onCancelBtnClick(): void;
  partialUpdateCallback(data: any): void;
}

export const Rejection: React.FC<RejectionProps> = ({
  onCancelBtnClick,
  docId,
  prejimkaType,
  resource,
  partialUpdateCallback,
}) => {
  const formatMessage = useMessage();

  const reasonItemsSource =
    prejimkaType &&
    (prejimkaType === PrejimkaTypesEnum.DMVS_GAD ||
      prejimkaType === PrejimkaTypesEnum.DTM)
      ? REJECTION_REASON_DTM_GAD
      : REJECTION_REASON_DEFAULT;
  const reasonItems = useSelectMessages(rejectionReasonMessages, reasonItemsSource);
  const [description, setDescription] = useState('');
  const [queryState, setQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  // eslint-disable-next-line @typescript-eslint/ban-types
  const [reason, setReason]: [REJECTION_REASON, Function] = useState(REJECTION_REASON.OTHER);

  const handleReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setReason(e.currentTarget.value);
  };
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDescription(e.currentTarget.value);
  };

  const handleReload = useCallback(() => resource.refetch(), [resource]);

  const handleSubmit = async () => {
    try {
      setQueryState(QUERY_STATE.WAITING);
      await partialUpdateCallback({
        query: { id: docId },
        request: {
          rejection_reason: reason,
          rejection_note: description,
        },
      });
      setQueryState(QUERY_STATE.SUCCESS);
      onCancelBtnClick();
      handleReload();
    } catch (err) {
      setQueryState(QUERY_STATE.FAIL);
      console.error(err);
    }
  };

  const handleCancelBtnClick = () => {
    onCancelBtnClick();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <div className='sg-a-p-4 sg-u-vs-2'>
        <PageTitle>
          <FormattedMessage {...rejectionMessages.reasonTitle} />
        </PageTitle>
        <RadioGroup value={reason} items={reasonItems} onChange={handleReasonChange} />
        <FormItem label={formatMessage(rejectionMessages.descriptionTitle)}>
          <Textarea value={description} onChange={handleDescriptionChange} />
        </FormItem>
        {queryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...rejectionMessages.alertFail} />
          </DangerAlert>
        )}
        {queryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...rejectionMessages.alertWaiting} />
          </WaitingAlert>
        )}
        <BtnBar>
          <CtaBtn type='submit'>
            <FormattedMessage {...rejectionMessages.btnSubmit} />
          </CtaBtn>
          <SecondaryBtn isDanger onClick={handleCancelBtnClick}>
            <FormattedMessage {...rejectionMessages.btnCancel} />
          </SecondaryBtn>
        </BtnBar>
      </div>
    </Form>
  );
};
